import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { DialogRef } from '../dialog/dialog-ref';
import { DialogConfig } from '../dialog/dialog-config';

@Component({
    selector: 'app-file-manager',
    templateUrl: './file-manager.component.html',
    styleUrls: ['./file-manager.component.scss'],
    standalone: false
})
export class FileManagerComponent implements OnInit {
  imageFiles: string[] = [];
  selectedImages: Set<string> = new Set();
  baseUrl = '';
  accessionIdentifier: string | undefined = '';

  sequenceMap: Record<string, number> = {};
  existingSequence = 0;
  nextSequence = 1;
  isMultiSelect = true;


  /**
   * Initializes the FileManagerComponent with required services and configuration.
   * If a baseUrl is provided through the dialog configuration, it will override the default value.
   * 
   * @param http - HttpClient service for making API requests to fetch files
   * @param dialogRef - Reference to the dialog instance for handling dialog actions (close, etc.)
   * @param config - Optional dialog configuration which may contain custom data including baseUrl
   */
  constructor(private http: HttpClient, private dialogRef: DialogRef, @Optional() @Inject(DialogConfig) private config?: DialogConfig){
    if (this.config?.data?.baseUrl) {
      this.baseUrl = this.config.data.baseUrl;
    }
    console.log('FileManager baseUrl →', this.baseUrl);
    this.accessionIdentifier = sessionStorage.getItem('procedureAccessionIdentifier') || '';
    console.log('Accession Identifier →', this.accessionIdentifier);

    this.existingSequence = +this.config?.data?.existingCount || 0;

    this.isMultiSelect = this.config?.data?.isMultiSelect !== undefined ? this.config.data.isMultiSelect : true;
  }

  ngOnInit(): void {
    this.nextSequence = 1;
    if(this.accessionIdentifier){
      this.http.get<any>(this.baseUrl + '/' +this.accessionIdentifier).subscribe(res => {
        this.imageFiles = res.files
          .filter((f: any) => this.isImage(f.name))
          .map((f: any) => f.name);
      });
    } else {
      console.error("Accession identifier is not available.");
    }
  }

  isImage(fileName: string): boolean {
    return /\.(png|jpe?g|gif|bmp|webp)$/i.test(fileName);
  }

  toggleSelection(file: string): void {
    const isSelected = this.selectedImages.has(file);

    // In single-select mode, always clear previous selections when choosing a new file.
    if (!this.isMultiSelect && !isSelected) {
      this.selectedImages.clear();
      this.sequenceMap = {};
    }

    if (isSelected) {
      // Always allow deselecting a file.
      this.selectedImages.delete(file);
      delete this.sequenceMap[file];
    } else {
      // Add the new file to the selection.
      this.selectedImages.add(file);
    }

    // Handle sequencing based on the mode.
    if (this.isMultiSelect) {
      this.resequence(); // Resequence the list in multi-select mode.
    } else if (this.selectedImages.size > 0) {
      // In single-select mode, the sequence is always 1.
      const selectedFile = this.selectedImages.values().next().value;
      this.sequenceMap[selectedFile] = 1;
    }
  }

  resequence() {
    const rem = Array.from(this.selectedImages)
    .sort((a,b)=>this.sequenceMap[a] - this.sequenceMap[b]);
    this.sequenceMap = {};
    rem.forEach((f,i)=> this.sequenceMap[f] = i+1);
    this.nextSequence = rem.length + 1;
  }

  onSequenceChange(file: string, raw: string) {
    const num = parseInt(raw, 10);
    if (!Number.isNaN(num) && this.selectedImages.has(file)) {
      this.sequenceMap[file] = num;
    }
  }

  /** New helper to detect invalid sequences */
  isSequenceInvalid(file: string): boolean {
    const seq = this.sequenceMap[file];
    const max = this.selectedImages.size;
    if (seq == null) {
      return false;
    }
    if (seq < 1 || seq > max) {
      return true;
    }
    const duplicates = this.getDuplicateSeqs();
    return duplicates.includes(seq);
  }

  hasInvalidSequence(): boolean {
    for (const file of this.selectedImages) {
      if (this.isSequenceInvalid(file)) {
        return true;
      }
    }
    return false;
  }

  getDuplicateSeqs(): number[] {
    const counts = new Map<number, number>();
    for (const seq of Object.values(this.sequenceMap)) {
      counts.set(seq, (counts.get(seq) || 0) + 1);
    }
    return Array.from(counts)
      .filter(([_seq, count]) => count > 1)
      .map(([seq, _count]) => seq);
  }

  /** Close dialog and return selected image URLs */
  onSubmit(): void {

    if (this.hasInvalidSequence()) return;

    const prefix = `${this.baseUrl}/${this.accessionIdentifier}`;
    const sortedFiles = Array.from(this.selectedImages).sort((a, b) => this.sequenceMap[a] - this.sequenceMap[b]);

    const payload = sortedFiles.map(file => ({
      imageUrl: `${prefix}/${file}`,
      seq: (this.sequenceMap[file] || 0) + this.existingSequence
    }));

    this.dialogRef.close(payload);
  }

  ngOnDestroy(): void {
    this.imageFiles = [];
    this.selectedImages.clear();
    this.sequenceMap = {};
    this.nextSequence = 1;
  }

}
